namespace log {
    function logToConsole(level: "error" | "warn" | "info", message: string, ...optionalParams: any[]) {
        let formattedMessage = `[${new Date().toLocaleTimeString()}] pm-proxy: ${message}`;
        console[level](formattedMessage, ...optionalParams);
    }

    export var error = logToConsole.bind(console, "error");
    export var warn = logToConsole.bind(console, "warn");
    export var info = logToConsole.bind(console, "info");
}

export namespace types {

    export type literal = number | void | string | boolean;
    export type ProxyProperty = literal | literal[] | StringIndexSignature | StringIndexSignature[];
    export type StringIndexSignature = { [prop: string]: ProxyProperty };
    /** Proxy types can only contains properties to work around limitations of the type system.
     *  This way the result of the invoke method is can be kept te same as the property type of the proxy target
     */
    export type ProxyTarget<T> = { [prop in keyof T]: ProxyProperty }
}


function generateRandomString(): string {
    //from https://stackoverflow.com/a/8084248
    return (Math.random() + 1).toString(16).substring(7)
}


namespace proxy {
    export interface IEventSource {
        on(method: string, callback: EventInvocationHandler): IDisposable;
    }

    export type EventInvocationHandler = (...args: any[]) => Promise<any> | void | any;
    export class EventBasedRequestHandler implements protocol.IRequestHandler, IEventSource {
        private readonly eventHandlers: { [methodName: string]: EventInvocationHandler[] } = {};

        handle(name: string, args: any[]): Promise<any> | void | any {
            const subscriptions = this.eventHandlers[name];
            if (false === Array.isArray(subscriptions)) {
                return;
            }
            const results: Promise<any>[] = [];

            subscriptions.forEach(sub => {
                const result = sub(args);
                if (result !== undefined) {
                    results.push(Promise.resolve(result));
                }
            });

            if (results.length > 0) {
                return Promise.race(results);
            }
        }

        on(method: string, callback: EventInvocationHandler) {
            let eventHandlers = this.eventHandlers;
            let subscriptions = eventHandlers[method] || (eventHandlers[method] = []);

            subscriptions.push(callback);

            return {
                dispose: () => {
                    let id = subscriptions.indexOf(callback);
                    if (id !== -1) {
                        subscriptions.splice(id, 1);
                    }
                    if (subscriptions.length < 1) {
                        //remove empty subscriptions array.
                        delete eventHandlers[method];
                    }
                }
            }
        }
    }


    export class ProxyMethodInvoker implements protocol.IRequestHandler {
        private target: any;

        /**
         *
         */
        constructor(target: object) {
            this.target = target;
        }

        handle(name: string, args: any[]): Promise<any> | void | any {
            if (false === (name in this.target)) {
                throw new Error(`Member ${name} not found`);
            }

            let result = this.target[name];

            if (typeof result === "function") {
                return result(...args);
            }

            if (Array.isArray(args) && args.length > 0) {
                throw new Error(`Arguments are provided for a non-function member`);
            }

            return result;
        }
    }



}


interface IDisposable {
    dispose(): void;
}

namespace protocol {

    export interface IRequestHandler {
        handle(name: string, args: any[]): Promise<any> | void | any;
    }


    /**
     * Represents an incoming message from the IFrame. After receiving this message, complete it using the `complete` function.
     * If response to the request is expected provide the complete function with the content of the response.
     */

    declare type IMessage = IRequestWireMessage | IReplyWireMessage;

    interface IRequestWireMessage {
        requestId: string,
        name: string;
        args?: any[];
    }

    interface IReplyWireMessage {
        replyId: string;
        payload?: any;
        error?: any;
    }


    export interface IMessagePort {
        onMessage(messageEventHandler: MessageEventHandler): IDisposable;
        postMessage(message: IMessage): void;

    }

    interface MessageEventHandler {
        handleRequest(request: IRequestWireMessage): void;
        handleReply(response: IReplyWireMessage): void;
    }

    interface IDeferred {
        resolve: (value?: any | PromiseLike<any>) => void;
        reject: (error: any) => void;
    }

    export class MessageSink implements IDisposable {
        private msgId: number = 0;
        private readonly proxyId: string = generateRandomString() + ".";
        private readonly pendingRequests: { [key: string]: IDeferred } = {};
        private disposer: IDisposable;


        /**
         *
         */
        constructor(private requestHandler: IRequestHandler | null, private messagePort: IMessagePort) {
            this.disposer = this.messagePort.onMessage({
                handleReply: (r) => this.handleReply(r),
                handleRequest: r => this.handleRequest(r)
            });
        }

        private handleRequest(request: IRequestWireMessage) {
            if (this.requestHandler == null) {
                return;
            }
            const { name, args: args, requestId: replyId } = request;
            let response: Promise<any>;

            try {
                const result = this.requestHandler.handle(name, args || []);
                response = Promise.resolve(result);
            } catch (error) {
                response = Promise.reject(error);
            }

            response
                .then(result => this.messagePort.postMessage({ replyId, payload: result }))
                .catch(reason => {
                    const error = `Handling request with name '${name}' failed. Reason: ${reason}`;
                    log.warn(error, reason);
                    this.messagePort.postMessage({ replyId, error });
                });
        }


        invoke<T>(method: string, args: any[]): Promise<T> {
            const msgId = this.msgId++;
            return new Promise((resolve, reject) => {
                log.info("Invoking method %s with args", method, args);
                const requestId = this.proxyId.concat(msgId.toString(10));

                this.pendingRequests[requestId] = { resolve, reject }

                this.messagePort.postMessage({ requestId, name: method, args });
            })
        }


        handleReply(reply: IReplyWireMessage) {
            const deferred = this.pendingRequests[reply.replyId];

            if (reply.error) {
                deferred.reject(reply.error);
            } else {
                deferred.resolve(reply.payload);
            }
            delete this.pendingRequests[reply.replyId];
        }

        dispose() {
            this.disposer.dispose();
        }
    }


    export function connectMessaging(targetWindow: Window, origin: string, receivingWindow: Window = window): IMessagePort {

        function isReplyMessage(msg: IReplyWireMessage): msg is IReplyWireMessage {
            return typeof msg.replyId === "string";
        }
        function isRequestMessage(msg: IRequestWireMessage): msg is IRequestWireMessage {
            return typeof msg.requestId === "string" && typeof msg.name === "string";
        }
        function originFilter(messageEventHandler: MessageEventHandler, ev: MessageEvent) {
            if (ev.origin !== origin && origin !== "*") {
                log.info("Discarding message from untrusted origin (%s)", ev.origin)
                return;
            }

            if (typeof ev.data !== "object") {
                log.info("Message data is not an object", ev.data);
                return;
            }

            if (isReplyMessage(ev.data)) {
                messageEventHandler.handleReply(ev.data);
            } else if (isRequestMessage(ev.data)) {
                messageEventHandler.handleRequest(ev.data);
            }
        }

        return {
            postMessage: (message: IMessage) => targetWindow.postMessage(message, origin),
            onMessage: (messageEventHandler: MessageEventHandler) => {
                const eventListener = originFilter.bind(null, messageEventHandler);
                receivingWindow.addEventListener("message", eventListener, false);
                log.info("Message event handler attached.");
                return {
                    dispose: () => {
                        console.info("Disposing message event handler");
                        return receivingWindow.removeEventListener("message", eventListener, false);
                    }
                }
            }
        };
    }
    export function connectMessagingToFrame(targetFrame: HTMLIFrameElement): IMessagePort {
        const targetWindow = targetFrame.contentWindow;
        const location = targetFrame.src; //e.g. http://localhost:<port>/asdasd
        const origin = getOrigin(location);
        log.info("Connect Messaging to IFrame. Origin: ", origin);

        

        return connectMessaging(targetWindow, origin);

        function getOrigin(src: string) {
            const anchorElement = document.createElement("a");
            anchorElement.href = src;
            let host = anchorElement.host;

            if (host.endsWith(":443") || host.endsWith(":80")) {
                // IE includes also the default ports. This is not standard, so remove it.
                host = host.substring(0, host.lastIndexOf(":"))
            }

            return `${anchorElement.protocol}//${host}`;
        }
    }
}


namespace builder {
    type RpcInvoker = <TR>(method: keyof any, ...args: any[]) => Promise<any>;

    interface RpcConnection {
        invoke: RpcInvoker;
    }


    interface RpcInvokerConstructor {
        new <TR>(messageSink: protocol.MessageSink): RpcConnection;
    }

    export interface ProxyConstructor {
        new(): object;
    }

    export interface CommunicationSetup {
        setupRemote<TRemote extends types.ProxyTarget<TRemote>>(): CommunicationSetupWithRemote<IMessagingProxy<TRemote>>;
        handleIncomingMessagesUsing<T extends ProxyConstructor>(messageHandler: T): this;
        start(): Connection;
    }
    export interface CommunicationSetupWithRemote<T extends RpcConnection> extends CommunicationSetup {
        start(): T & Connection;
    }

    export interface Connection {
        stop(): void;
    }

    export function create(portBuilder: () => protocol.IMessagePort): CommunicationSetup {
        return new Builder(portBuilder)
    }

    class Builder {
        private requestHandler: protocol.IRequestHandler | null = null;

        constructor(private portBuilder: () => protocol.IMessagePort) {

        }


        handleIncomingMessagesUsing(target: ProxyConstructor): this {
            this.requestHandler = new proxy.ProxyMethodInvoker(new target());

            return this;
        }

        setupRemote<TRemote extends types.ProxyTarget<TRemote>>(): CommunicationSetupWithRemote<IMessagingProxy<TRemote>> {
            var newBuilder = new BuilderWithRemote<IMessagingProxy<TRemote>>(this.portBuilder, MessagingProxy);
            newBuilder.requestHandler = this.requestHandler;
            return newBuilder;
        }

        protected setup(): [Connection, protocol.MessageSink] {
            var port = this.portBuilder();

            var sink = new protocol.MessageSink(this.requestHandler, port);

            var connection: Connection = {
                stop: sink.dispose
            };

            return [connection, sink];
        }

        start(): Connection {
            var [connection] = this.setup();
            
            return connection;
        }
    }

    class BuilderWithRemote<TRemote extends RpcConnection> extends Builder {

        constructor(portBuilder: () => protocol.IMessagePort, private proxyConstr: RpcInvokerConstructor) {
            super(portBuilder);

        }

        start(): TRemote & Connection {
            var [connection, sink] = super.setup();

            var rpcConnection = <TRemote & Connection>new this.proxyConstr(sink);
            rpcConnection.stop = connection.stop;
            return rpcConnection;
        }
    }
}


export class MessagingProxy<TRemote extends types.ProxyTarget<TRemote>> implements IMessagingProxy<TRemote> {

    constructor(private messageSink: protocol.MessageSink) {

    }

    invoke<MethodName extends keyof TRemote>(method: Extract<MethodName, string>, ...args: any[]): Promise<TRemote[MethodName]> {
        return this.messageSink.invoke(method, args);
    }
}

export interface IMessagingProxy<TRemote extends types.ProxyTarget<TRemote>> {
    invoke<MethodName extends keyof TRemote>(method: MethodName, ...args: any[]): Promise<TRemote[MethodName]>;
}


export function setupCommunicationTo(window: Window, origin: string = "*"): builder.CommunicationSetup {
    return builder.create(() => protocol.connectMessaging(window, origin));
}

export function setupCommunicationToOpener(origin: string = "*"): builder.CommunicationSetup {
    return builder.create(() => protocol.connectMessaging(window.opener, origin));
}
export function setupCommunicationToParent(origin: string = "*"): builder.CommunicationSetup {
    return builder.create(() => protocol.connectMessaging(window.parent, origin));
}

export function setupCommunicationToFrame(targetFrame: HTMLIFrameElement): builder.CommunicationSetup {

    return builder.create(() => protocol.connectMessagingToFrame(targetFrame));
}



// WEBPACK FOOTER //
// ./ClientApp/app/services/cap-proxy/index.ts